import axios, { AxiosResponse } from 'axios';
import { CalibrationFeedProgram } from 'views/bluviu-nutrition/features/calibrations/types/calibration-feed-program.type';
import { CloneCalibrationRequest } from 'views/bluviu-nutrition/features/calibrations/types/clone-calibration-request.type';
import { CreateCalibrationRequest } from 'views/bluviu-nutrition/features/calibrations/types/create-calibration-request.type';
import { R2Calibration } from 'views/bluviu-nutrition/features/calibrations/types/r2-calibration.type';
import { SeaCloudCage } from 'views/bluviu-nutrition/features/calibrations/types/sea-cloud-cage.type';
import { UpdateCalibrationFeedProgramRequest } from 'views/bluviu-nutrition/features/calibrations/types/update-calibration-feed-program-request.type';
import { UpdateR2CalibrationInputsRequest } from 'views/bluviu-nutrition/features/calibrations/types/update-r2-calibration-inputs-request.type';

/**
 * Get a multi cage calibration
 * @param globalSiteId
 * @param calibrationId
 * @returns
 */
export const getR2Calibration = async (globalSiteId: string, calibrationId: string): Promise<R2Calibration> => {
  const res = await axios.get<R2Calibration>(
    `/ewosapi/v1/calibration/gt2/multi?globalSiteId=${globalSiteId}&calibrationId=${calibrationId}`
  );
  return res.data;
};

/**
 * Create a multi cage calibration
 * @param globalSiteId
 * @param request
 * @returns
 */
export const postR2Calibration = async (
  globalSiteId: string,
  request: CreateCalibrationRequest
): Promise<R2Calibration> => {
  const res = await axios.post(`/ewosapi/v1/calibration/gt2/multi?globalSiteId=${globalSiteId}`, request);

  return res.data;
};

export const cloneR2Calibration = async (
  globalSiteId: string,
  request: CloneCalibrationRequest
): Promise<R2Calibration> => {
  //   return CalibrationData;
  const res = await axios.post(`/ewosapi/v1/calibration/gt2/multi/clone?globalSiteId=${globalSiteId}`, request);
  return res.data;
};

/**
 * Update the calibration settings for the Site with the specified globalSiteId.
 * @param globalSiteId The site to be calibrated
 * @param request `UpdateCalibrationSettingsRequest`
 * @returns `R2Calibration`
 */
export const putR2Calibration = async (
  globalSiteId: string,
  calibrationId: string,
  request: UpdateR2CalibrationInputsRequest
): Promise<R2Calibration> => {
  const res = await axios.put(
    `/ewosapi/v1/calibration/gt2/multi?globalSiteId=${globalSiteId}&calibrationId=${calibrationId}`,
    request
  );
  return res.data;
};

/**
 * Gets a list of SeaCloud generations in the form of years, to be used in Multi Cage Calibration Inputs section
 * @param globalSiteId
 * @param request
 * @returns `number[]`
 */
export const getSeaCloudGenerations = async (globalSiteId: string): Promise<number[]> => {
  const res = await axios.get<number[]>(`/ewosapi/v1/calibration/seacloud/generations?globalSiteId=${globalSiteId}`);

  return res.data;
};

/**
 * Gets a list of SeaCloud cages, to be used in Multi Cage Calibration Inputs section
 * @param globalSiteId
 * @param request
 * @returns `SeaCloudCage[]`
 */
export const getSeaCloudCages = async (globalSiteId: string, generation: number): Promise<SeaCloudCage[]> => {
  const res = await axios.get<SeaCloudCage[]>(
    `/ewosapi/v1/calibration/seacloud/generation-results?globalSiteId=${globalSiteId}&generation=${generation}`
  );

  return res.data;
};

/**
 * Get the Feed Program for a simple calibration
 * @param globalSiteId
 * @param calibrationId
 * @returns `CalibrationFeedProgram[]`
 */
export const getR2CalibrationFeedProgram = async (
  globalSiteId: string,
  calibrationId: string
): Promise<CalibrationFeedProgram[]> => {
  const res = await axios.get<CalibrationFeedProgram[]>(
    `/ewosapi/v1/calibration/gt2/multi/feed-program?globalSiteId=${globalSiteId}&calibrationId=${calibrationId}`
  );

  return res.data;
};

/**
 *
 * Update a multi cage calibration feed program
 * @param globalSiteId
 * @param calibrationId
 * @param request
 * @returns
 */
export const putR2CalibrationFeedProgram = async (
  globalSiteId: string,
  calibrationId: string,
  request: UpdateCalibrationFeedProgramRequest[]
): Promise<AxiosResponse> => {
  const res = await axios.put(
    `/ewosapi/v1/calibration/gt2/multi/feed-program?globalSiteId=${globalSiteId}&calibrationId=${calibrationId}`,
    request
  );
  return res.data;
};

/**
 *
 * Gets a calibration input file. Requires being a Super User
 * @param calibrationId
 * @returns
 */
export const getR2CalibrationInputFile = async (calibrationId: string): Promise<AxiosResponse> => {
  return await axios.get<AxiosResponse>(`/ewosapi/v1/calibration/gt2/multi/file/input?calibrationId=${calibrationId}`, {
    responseType: 'blob',
  });
};

/**
 *
 * Gets the calibration output file. Requires being a Super User
 * @param calibrationId
 * @returns
 */
export const getR2CalibrationOutputFile = async (calibrationId: string): Promise<AxiosResponse> => {
  return await axios.get<AxiosResponse>(`/ewosapi/v1/calibration/gt2/multi/file/output?calibrationId=${calibrationId}`, {
    responseType: 'blob',
  });
};

/**
 * Trigger a multi cage calibration for a site. This begins the process of a calibration and can be monitored with the `getBluViuNutritionCalibrationStatus` api call.
 * @param globalSiteId
 * @returns `AxiosResponse`
 */
export const runR2Calibration = async (globalSiteId: string, calibrationId: string): Promise<AxiosResponse> => {
  return await axios.put(`/ewosapi/v1/calibration/gt2/multi/calibrate?globalSiteId=${globalSiteId}&calibrationId=${calibrationId}`);
};

/**
 * Get the status of a multi cage calibration for a given site.
 * @param globalSiteId
 * @returns
 */
export const getBluViuNutritionCalibrationStatus = async (
  globalSiteId: string,
  calibrationId: string
): Promise<{ calibrationIsRunning: boolean }> => {
  const res = await axios.get<{ calibrationIsRunning: boolean }>(
    `/ewosapi/v1/calibration/gt2/multi/status?globalSiteId=${globalSiteId}&calibrationId=${calibrationId}`
  );
  return res.data;
};
